import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import StatusVerifiedIcon from '@atlaskit/icon/core/status-verified';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import { token } from '@atlaskit/tokens';
import { AuthoritativeAnswerAttribution } from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution';
import { buildAuthoritativeAnswerPanelShownEvent } from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution/authoritative-answer-panel-shown-event';
import {
	AUTHORITY_FEEDBACK_ORIGIN_SAIN,
	FEEDBACK_ENTRYPOINT_ID_SAIN,
	submitAuthorityFeedback,
} from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution/submit-authority-feedback';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import { AIAnswerError } from '../../../ai-answer-dialog/ai-answer-body/ai-answer-result/ai-answer-error';
import { aiAnswerCollapseClicked } from '../../../ai-answer-dialog/ai-answer-collapse-clicked';
import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { aiAnswerExpandClicked } from '../../../ai-answer-dialog/ai-answer-expand-clicked';
import { SmartAnswerFooterSection } from '../../../ai-answer-dialog/ai-answer-footer/ai-answer-footer';
import { FollowUpSuggestionFooter } from '../../../ai-answer-dialog/ai-answer-footer/followup-suggestion-footer';
import { getSearchAIAnalyticsContextData } from '../../../ai-answer-dialog/analytics';
import { onAIAnswerResultCompleteShown } from '../../../ai-answer-dialog/on-ai-answer-result-complete-shown';
import { onAIAnswerResultFirstByteShown } from '../../../ai-answer-dialog/on-ai-answer-result-first-byte-shown';
import { onAuthoritativeSourceClick } from '../../../ai-answer-dialog/on-authoritative-source-click';
import { SearchAIAnalyticsContext } from '../../../ai-answer-dialog/search-ai-analytics-context';
import { SearchAIAnalyticsContextLocation } from '../../../ai-answer-dialog/search-ai-analytics-context-location';
import {
	type commonAttributesType,
	type NLPSearchResultSource,
	type SourceSmartCardType,
} from '../../../ai-answer-dialog/types';
import { SainAction } from '../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { AISourcesCarousel } from '../../../common/ui/ai-sources';
import { useKnowledgeSmartCardAnalytics } from '../../../common/ui/knowledge-smart-card/hooks/use-knowledge-smart-card-analytics';
import { SourcesBar } from '../../../common/ui/sources-button/sources-bar';
import { SourcesSidePanel } from '../../../common/ui/sources-side-panel/sources-side-panel';
import { SAINFeatureControl } from '../../../common/utils/sain-feature-control';
import type { QueryIntent } from '../../../constants';
import { extractUrlHostname } from '../../../extract-url-hostname';
import { useBufferedContent } from '../../common/hooks/use-buffered-content';
import { useContentTruncationDetector } from '../../common/hooks/use-content-truncation-detector';
import { useDelayedResizeObserver } from '../../common/hooks/use-delayed-resize-observer';
import { AICollapse } from '../../common/ui/ai-collapse';
import { AIDisclaimerAndFeedback } from '../../common/ui/ai-disclaimer-and-feedback';
import { AnimatedSingleLineShowMore } from '../../common/ui/animated-single-line-show-more';
import { OpenChatInputBox } from '../../common/ui/open-chat-input-box';
import { ReactRenderer } from '../../common/ui/react-renderer';
import { ReadMoreButton } from '../../common/ui/read-more-button';
import { messages } from '../messages';
import { onAIAnswerMinimizedCollapseClicked } from './analytics/on-ai-answer-minimized-collapse-clicked';
import { onAIAnswerMinimizedExpandClicked } from './analytics/on-ai-answer-minimized-expand-clicked';
import { onAIAnswerMinimizedScrollUpClicked } from './analytics/on-ai-answer-minimized-scroll-up-clicked';
import { SmartAnswerCardMinimizedLoading } from './loading/smart-answer-card-minimized-loading';
import { SmartAnswerReady } from './smart-answer-ready';

// Memoized renderer component outside main component
const MemoizedRenderer = React.memo(function MemoizedRenderer({
	contentToRender,
	showAnimation,
}: {
	contentToRender: string;
	showAnimation: boolean;
}) {
	return (
		<ReactRenderer
			key="stable-renderer"
			markdown={contentToRender}
			animateComponents={showAnimation}
		/>
	);
});

const styles = cssMap({
	// Force the smart answer card to be the full max width of
	// the search page layout body main content container
	fullWidth: {
		width: '100vw',
		maxWidth: '100%',
		marginBottom: token('space.150'),
	},
	interactive: {
		cursor: 'pointer',
	},
	root: {
		minHeight: '198px',
	},
	fullHeight: {
		height: '100%',
	},
	disclaimer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
	followUpChatInputSectionStyles: {
		marginTop: token('space.200'),
	},
	followUpChatInputSectionWithBarStyles: {
		marginTop: token('space.100'),
	},
	disclaimerWrapperStyles: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingTop: token('space.050'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
	},
	sectionContainer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
	sourcesTopPadding: {
		paddingTop: token('space.050'),
	},
	linkListContainer: {
		marginTop: token('space.075'),
	},
	nonInteractive: {
		pointerEvents: 'none',
	},
	loadingPaddingOverrides: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	standardSAINMarginOverrides: {
		marginBottom: token('space.100'),
	},
});

type SmartAnswerCardMinimizedProps = {
	onFMPReached?: () => void;
	onAnswerExpandedChange?: (expanded: boolean) => void;
	/**
	 * Called whenever the answer is minimized (hidden via the "Hide answer" button)
	 * or un-minimized (expanded via the in-flow SmartAnswerReady or the floating
	 * SmartAnswerReady's "Scroll up" button). Used by the parent to gate dwell
	 * tracking so a minimized answer is not counted as "viewed".
	 */
	onAnswerMinimizedChange?: (minimized: boolean) => void;
	queryIntent?: QueryIntent;
	floatingSmartAnswerReadyPortalRef?: React.RefObject<HTMLDivElement>;
};

function SmartAnswerCardMinimizedComponent({
	onFMPReached,
	onAnswerExpandedChange,
	onAnswerMinimizedChange,
	queryIntent,
	floatingSmartAnswerReadyPortalRef,
}: SmartAnswerCardMinimizedProps) {
	// Tracks whether the user clicked "Expand answer" while the answer was loading
	// Used to decide if the answer should appear in the collapsed or fully expanded state
	// when streaming begins, and is also kept in sync with isAnswerMinimized after loading so the
	// show-more button is hidden when the answer is intentionally expanded.
	// - false (default): user has NOT requested expansion during loading
	// - true: user clicked "Expand answer" during loading
	const [userExpandedDuringLoading, setUserExpandedDuringLoading] = useState(false);
	// Tracks whether the answer is currently "minimized" — i.e. the loaded answer is
	// hidden and the SmartAnswerReady component is shown.
	// This is distinct from `userExpandedDuringLoading` which is about the loading-state
	// expand/collapse toggle. While loading this is always false; it only flips to
	// true post-load when the user explicitly hides the answer.
	const [isAnswerMinimized, setIsAnswerMinimized] = useState(false);

	// state for whether loaded answer is fully expanded or collapsed
	const [collapsed, setCollapsed] = useState(true);

	const containerRef = useRef<HTMLDivElement>(null);
	const [didUserTriggerCollapse, setDidUserTriggerCollapse] = useState(false);
	const [isVisibleTextComplete, setIsVisibleTextComplete] = useState(false);
	const [showAnimation, setShowAnimation] = useState(false);

	const componentRenderTimeRef = useRef<number>(Date.now());
	const firstByteEventFiredRef = useRef(false);

	const context = useAIAnswerContext();
	const { commonAttributes, hasError, isAnswered, loading, partialResponse, luminaContentMap } =
		context;
	const { queryHash, searchSessionId, answerString, sources, errorStateSuggestedQueries } =
		commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const streamingText = partialResponse?.partialNlpResult ?? '';
	const [streamingTextToDisplay, setStreamingTextToDisplay] = useState<string>(streamingText);

	// Refs for content truncation detection
	const isVisibleTextCompleteRef = useRef(false);
	// Measures only answer text height (excludes sources and chat input) so onReadMoreButtonShown
	// fires only when the text itself is truncated, not due to other content
	const textContentRef = useRef<HTMLDivElement>(null);
	const completeAnswerAnalyticsEventFiredRef = useRef(false);
	const renderedContentRef = useRef<HTMLDivElement>(null);
	const contentContainerRef = useRef<HTMLDivElement>(null);

	const hasLinksInAnswer =
		!!(luminaContentMap && Object.keys(luminaContentMap).length > 0) &&
		fg('kd_sain_enable_inline_lumina');

	useEffect(() => {
		// If streaming stopped but buffered content still exists, clear it to reset the display
		if (!streamingText && streamingTextToDisplay && fg('kd_fix_abort_errors')) {
			setStreamingTextToDisplay('');
			setIsVisibleTextComplete(false);
			return;
		}
		if (collapsed && !isVisibleTextComplete) {
			setStreamingTextToDisplay(streamingText);
		} else if (!collapsed) {
			setStreamingTextToDisplay(streamingText);
		}
	}, [collapsed, isVisibleTextComplete, streamingText, streamingTextToDisplay]);

	// Notify context when collapsed state changes
	useEffect(() => {
		onAnswerExpandedChange?.(!collapsed);
	}, [collapsed, onAnswerExpandedChange]);

	useEffect(() => {
		if (!loading && !hasError && !isVisibleTextComplete) {
			setIsVisibleTextComplete(true);
			onFMPReached?.(); // Mark FMP as reached for cached answers
		}
	}, [loading, hasError, isVisibleTextComplete, onFMPReached]);

	// Track if we should show animations (if loading or partialResponse are true)
	useEffect(() => {
		if (!showAnimation && loading && partialResponse) {
			setShowAnimation(true);
		}
		if (loading && !partialResponse && showAnimation && fg('kd_fix_abort_errors')) {
			setShowAnimation(false);
			firstByteEventFiredRef.current = false;
			completeAnswerAnalyticsEventFiredRef.current = false;
		}
	}, [loading, partialResponse, showAnimation]); // use useEffect to trigger analytics event in order to avoid needing to
	// pass commonAttributes as a dependency to handleExpandCollapseButtonClick
	// and thus avoid re-rendering SmartAnswerCardBodyStreaming with every
	// update to commonAttributes (which gets updated with the streamed text)
	useEffect(() => {
		if (didUserTriggerCollapse) {
			fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes));
		}
	}, [didUserTriggerCollapse, commonAttributes, fireAnalyticsEvent]);

	// Detect if content is truncated when collapsed
	const isContentTruncated = useContentTruncationDetector(
		renderedContentRef,
		contentContainerRef,
		collapsed,
		[streamingText],
	);

	const isTextTruncated = useContentTruncationDetector(
		textContentRef,
		contentContainerRef,
		collapsed,
		[streamingText],
	);

	// Complete answer analytics
	useEffect(() => {
		// Only fire once and ensure DOM element is ready with meaningful content
		if (completeAnswerAnalyticsEventFiredRef.current || !renderedContentRef.current) {
			return;
		}
		const answerHeight = renderedContentRef.current.getBoundingClientRect().height;
		const containerHeight = contentContainerRef.current?.getBoundingClientRect().height ?? 0;
		const whitespaceHeight = Math.max(containerHeight - answerHeight, 0);

		// Only fire if we have meaningful height (content is rendered)
		if (answerHeight > 0 && containerHeight > 0) {
			fireAnalyticsEvent(
				onAIAnswerResultCompleteShown({
					...commonAttributes,
					answerHeight,
					containerHeight,
					whitespaceHeight,
					queryIntent: queryIntent,
				}),
			);
			completeAnswerAnalyticsEventFiredRef.current = true;
		}
	}, [fireAnalyticsEvent, commonAttributes, queryIntent, isContentTruncated]);

	// SAIN usage event
	useEffect(() => {
		fireSAINUsageEvent({ commonAttributes });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Callback bound to the expand/collapse buttons in SmartAnswerCardMinimizedLoading.
	// Only toggles `userExpandedDuringLoading`; never sets `isAnswerMinimized` (which is only
	// used for the post-load minimize flow). Tracks whether the user has requested expansion
	// during loading.
	const handleFoldAnswerButtonClick = useCallback(() => {
		setUserExpandedDuringLoading((prev) => !prev);
	}, []);

	// Tracks whether the answer card is currently within the viewport
	// Used to decide whether to render the SmartAnswerReady component at the top of the search
	// results when the user has scrolled the answer card out of view.
	const [isCardInViewport, setIsCardInViewport] = useState(true);

	// Scrolls the answer card back into view and expands it fully.
	// Resets isAnswerMinimized so the answer is un-minimized, and sets
	// userExpandedDuringLoading=true so the in-flow card renders fully expanded.
	const handleScrollUpClick = useCallback(() => {
		fireAnalyticsEvent(onAIAnswerMinimizedScrollUpClicked(commonAttributes));
		setIsAnswerMinimized(false);
		setUserExpandedDuringLoading(true);
		setCollapsed(false);
		const node = containerRef.current;
		if (!node) {
			return;
		}
		// The "Scroll up" button only appears on the floating banner, which is only
		// rendered when the card is scrolled out of view (isCardInViewport === false).
		// So reaching this handler means the card is effectively out of view (or hidden
		// behind the fixed banner) and we should always scroll it back to the top.
		//
		// We intentionally avoid an isOutOfViewport short-circuit here: the previous
		// `rect.bottom <= 0` guard failed the edge case where the card's bottom edge
		// sits a few pixels below the viewport top (positive rect.bottom) but the card
		// is still visually hidden behind the fixed floating banner — in that case the
		// guard skipped the scroll and the user was never taken back to the answer.
		node.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, [commonAttributes, fireAnalyticsEvent]);

	// Notify parent (ai-answer-dialog) whenever the "answer minimized" state changes
	// so dwell tracking can be gated on whether the user can actually see the answer.
	useEffect(() => {
		onAnswerMinimizedChange?.(isAnswerMinimized);
	}, [isAnswerMinimized, onAnswerMinimizedChange]);

	// Toggles the answer minimized state for the "Hide answer" and in-flow "Expand"
	// buttons. `minimized=true` hides the answer behind the SmartAnswerReady component;
	// `minimized=false` re-shows the answer in fully expanded form. Also resets the
	// loading-time expand state so visual states stay in sync.
	const handleAnswerHiddenToggle = useCallback(
		(minimized: boolean) => {
			if (minimized) {
				fireAnalyticsEvent(onAIAnswerMinimizedCollapseClicked(commonAttributes));
			} else {
				fireAnalyticsEvent(onAIAnswerMinimizedExpandClicked(commonAttributes));
			}
			setIsAnswerMinimized(minimized);
			// userExpandedDuringLoading mirrors the inverse — minimized=true means the user
			// has NOT expanded; minimized=false means they have explicitly expanded.
			setUserExpandedDuringLoading(!minimized);
			setCollapsed(minimized);
		},
		[commonAttributes, fireAnalyticsEvent],
	);

	// callback to expand/collapse loaded answer
	const handleExpandCollapseButtonClick = useCallback(() => {
		if (collapsed) {
			setCollapsed(false);
			setDidUserTriggerCollapse(false);
		} else {
			setCollapsed(true);
			setDidUserTriggerCollapse(true);
		}
	}, [collapsed]);

	const handleVisibleTextComplete = useCallback(() => {
		setIsVisibleTextComplete(true);
		onFMPReached?.(); // Mark FMP as reached for streaming answers
	}, [onFMPReached]);

	const handleContainerClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			const selection = window.getSelection()?.toString() ?? '';
			// Highlighting text should not expand the card
			const hasHighlightedText = selection.length > 0;
			// Clicking in a feedback modal or flag should not expand the card
			const isClickingOnContainer = containerRef.current?.contains(event.target as Node) ?? false;

			if (collapsed && !hasHighlightedText && isClickingOnContainer) {
				setCollapsed(false);
				// Clicking anywhere in the container while loading is treated the same as
				// clicking "Expand answer" — so the show more button hides for the rest of
				// the streaming session.
				setUserExpandedDuringLoading(true);
				fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
				fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
			}
		},
		[collapsed, fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent],
	);

	const { handleSmartCardClick } = useKnowledgeSmartCardAnalytics({
		aiAnswerDialogContext: context,
	});

	// UFO resize observer for streaming
	const resizeObserverCallback = useCallback(() => {
		if (
			!isVisibleTextCompleteRef.current &&
			renderedContentRef.current &&
			contentContainerRef.current &&
			!!streamingTextToDisplay
		) {
			const renderedContentRefHeight = renderedContentRef.current.getBoundingClientRect().height;
			const contentContainerRefHeight = contentContainerRef.current.getBoundingClientRect().height;
			const isVisibleTextComplete = renderedContentRefHeight > contentContainerRefHeight;
			if (isVisibleTextComplete) {
				handleVisibleTextComplete();
				isVisibleTextCompleteRef.current = true;
			}
		}
	}, [handleVisibleTextComplete, streamingTextToDisplay]);
	// eslint-disable-next-line react-hooks/rules-of-hooks
	useDelayedResizeObserver(renderedContentRef, resizeObserverCallback, 100, [
		streamingTextToDisplay,
	]);

	const bufferedContent = useBufferedContent(streamingTextToDisplay, isAnswered && !loading);

	// If the user clicked "Expand answer" during loading, show the answer uncollapsed
	// as soon as streaming begins (bufferedContent has content) and keep it uncollapsed when done.
	useEffect(() => {
		if (userExpandedDuringLoading && bufferedContent.length > 0) {
			setCollapsed(false);
		}
	}, [userExpandedDuringLoading, bufferedContent]);

	// Track whether the answer is in the viewport so we can render
	// SmartAnswerReady when the user has scrolled past the answer card.
	// Do not show SmartAnswerReady if the user has already seen the answer
	// in expanded state.
	useEffect(() => {
		if (!isAnswered || hasError || isAnswerMinimized) {
			return;
		}
		if (!containerRef.current) {
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					setIsCardInViewport(entry.isIntersecting);
				});
			},
			{ threshold: 0 },
		);
		observer.observe(containerRef.current);
		return () => {
			observer.disconnect();
		};
	}, [isAnswered, hasError, isAnswerMinimized]);

	useEffect(() => {
		if (bufferedContent.length > 0 && !firstByteEventFiredRef.current) {
			fireAnalyticsEvent(
				onAIAnswerResultFirstByteShown({
					...commonAttributes,
					timeToFirstByte: Date.now() - componentRenderTimeRef.current,
				}),
			);
			firstByteEventFiredRef.current = true;
		}
	}, [bufferedContent, commonAttributes, fireAnalyticsEvent]);

	// Use answerString when complete, bufferedContent when streaming
	const contentToRender = isAnswered ? answerString : bufferedContent;

	// Experiments
	const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
		abTestId: 'kd_sain_show_no_answer_suggestions_exp',
	});

	const showReadMoreButton = collapsed && isContentTruncated;

	// Single return statement with React component for main content
	const isStreaming = !isAnswered && (loading || loading === undefined);

	// Early return for error state
	if (hasError) {
		return (
			<Box xcss={styles.fullWidth}>
				<AIAnswerError />
			</Box>
		);
	}

	// Early return for generating state
	if (!isAnswered && (loading || loading === undefined)) {
		if (!partialResponse || bufferedContent.length < 1) {
			return (
				<UFOSegment name="search-ai-dialog-all-text">
					<UFOLoadHold name="search-ai-dialog-all-text-loading" hold />
					<UFOSegment name="search-ai-dialog-visible-text">
						<UFOLoadHold name="search-ai-dialog-visible-text-loading" hold />
						<SmartAnswerCardMinimizedLoading
							onExpandClick={handleFoldAnswerButtonClick}
							userExpandedDuringLoading={userExpandedDuringLoading}
							commonAttributes={commonAttributes}
						/>
					</UFOSegment>
				</UFOSegment>
			);
		}
	}

	// When the user has minimized the answer (clicked "Hide answer" on the loaded card),
	// render the SmartAnswerReady component in place of the full answer.
	if (isAnswerMinimized) {
		return <SmartAnswerReady onExpandClick={() => handleAnswerHiddenToggle(false)} />;
	}

	// Render SmartAnswerReady at the top of the search results when:
	// - the answer has finished loading and is not in an error/no-answer state
	// - the answer card is currently scrolled out of the viewport
	// - the answer is currently in its collapsed state
	// - do not render if user expanded then collapsed answer
	const notifySmartAnswerReady = isAnswered && !hasError && !isCardInViewport && collapsed;

	return (
		<>
			<Box
				xcss={cx(
					styles.fullWidth,
					(fg('kd_team_26_sain_borderless_update') || SAINFeatureControl.isSAINUXUpliftEnabled()) &&
						styles.loadingPaddingOverrides,
				)}
				testId="smart-answer-card-minimized"
			>
				<UFOSegment name="search-ai-dialog-all-text">
					<UFOLoadHold name="search-ai-dialog-all-text-loading" hold={loading} />
					<UFOSegment name="search-ai-dialog-visible-text">
						<UFOLoadHold
							name="search-ai-dialog-visible-text-loading"
							hold={!isVisibleTextComplete}
						/>
						{fg('platform_ufo_custom_cohort_data_migration') ? (
							<>
								<UFOCustomCohortData dataKey="queryHash" value={queryHash} />
								<UFOCustomCohortData dataKey="searchSessionId" value={searchSessionId} />
								<UFOCustomCohortData dataKey="queryIntent" value={queryIntent} />
							</>
						) : (
							<UFOCustomData
								data={{
									queryHash,
									searchSessionId,
									queryIntent,
								}}
							/>
						)}
						{/* Main content */}
						<SmartAnswerCardMinimizedContent
							isStreaming={isStreaming}
							collapsed={collapsed}
							showReadMoreButton={showReadMoreButton}
							handleExpandCollapseButtonClick={handleExpandCollapseButtonClick}
							onHideAnswer={() => handleAnswerHiddenToggle(true)}
							contentContainerRef={contentContainerRef}
							renderedContentRef={renderedContentRef}
							textContentRef={textContentRef}
							isTextTruncated={isTextTruncated}
							sources={sources}
							searchSessionId={searchSessionId}
							sourceClickCallback={handleSmartCardClick}
							errorStateSuggestedQueries={errorStateSuggestedQueries}
							serveErrorStateSuggestedQueriesExp={serveErrorStateSuggestedQueriesExp}
							commonAttributes={commonAttributes}
							contentToRender={contentToRender}
							showAnimation={showAnimation}
							handleContainerClick={handleContainerClick}
							containerRef={containerRef}
							hasLinksInAnswer={hasLinksInAnswer}
							userExpandedDuringLoading={userExpandedDuringLoading}
						/>
					</UFOSegment>
				</UFOSegment>
			</Box>
			{notifySmartAnswerReady &&
				floatingSmartAnswerReadyPortalRef?.current &&
				createPortal(
					<SmartAnswerReady
						appearance="scrollUp"
						onScrollUpClick={handleScrollUpClick}
						testId="smart-answer-ready-floating"
					/>,
					floatingSmartAnswerReadyPortalRef.current,
				)}
		</>
	);
}

function SmartAnswerCardMinimizedContent({
	isStreaming,
	collapsed,
	showReadMoreButton,
	handleExpandCollapseButtonClick,
	contentContainerRef,
	renderedContentRef,
	textContentRef,
	isTextTruncated,
	sources,
	searchSessionId,
	sourceClickCallback,
	errorStateSuggestedQueries,
	serveErrorStateSuggestedQueriesExp,
	commonAttributes,
	contentToRender,
	showAnimation,
	onHideAnswer,
	handleContainerClick,
	containerRef,
	hasLinksInAnswer,
	userExpandedDuringLoading,
}: {
	isStreaming: boolean;
	collapsed: boolean;
	showReadMoreButton: boolean;
	handleExpandCollapseButtonClick: () => void;
	contentContainerRef: React.RefObject<HTMLDivElement>;
	renderedContentRef: React.RefObject<HTMLDivElement>;
	textContentRef: React.RefObject<HTMLDivElement>;
	isTextTruncated: boolean;
	sources: NLPSearchResultSource[];
	searchSessionId: string;
	sourceClickCallback: ({
		index,
		source,
		hostname,
		sourceCardType,
		isAuxClick,
		isContextMenu,
	}: {
		index: number;
		source: NLPSearchResultSource;
		hostname?: string;
		sourceCardType: SourceSmartCardType;
		isAuxClick?: boolean;
		isContextMenu?: boolean;
	}) => void;
	errorStateSuggestedQueries: string[];
	serveErrorStateSuggestedQueriesExp: boolean;
	commonAttributes: commonAttributesType;
	contentToRender: string;
	showAnimation: boolean;
	onHideAnswer: () => void;
	handleContainerClick: (event: React.MouseEvent<HTMLDivElement>) => void;
	containerRef: React.RefObject<HTMLDivElement>;
	hasLinksInAnswer: boolean;
	/**
	 * Mirrors the parent's `userExpandedDuringLoading` state — true means the user
	 * clicked "Expand answer" while loading and we should not show the show-more button.
	 */
	userExpandedDuringLoading: boolean;
}) {
	const { formatMessage } = useIntl();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const { authorityUiMetadata } = useAIAnswerContext();
	const { cloudId, queryHash, extraAttributes, source } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const isHighlyAuthoritative =
		fg('rovo_authoritative_answer_attribution') && !!authorityUiMetadata?.isHighlyAuthoritative;

	const handleAuthorityPanelShown = useCallback(() => {
		fireAnalyticsEvent(
			buildAuthoritativeAnswerPanelShownEvent({
				product: 'sain',
				source,
				correlationAttributes: {
					cloudId,
					searchSessionId,
					queryHash,
					traceId: extraAttributes?.requestId,
				},
				authorityUiMetadata,
			}),
		);
	}, [
		fireAnalyticsEvent,
		source,
		cloudId,
		searchSessionId,
		queryHash,
		extraAttributes?.requestId,
		authorityUiMetadata,
	]);

	const fireAuthorityLinkClick = useCallback(
		(linkType: 'source' | 'expert') => {
			fireAnalyticsEvent(
				onAuthoritativeSourceClick({
					...commonAttributes,
					linkType,
					authorityUiMetadata,
				}),
			);

			// Credits the click towards SAIN's success metric (SSRv5).
			fireSAINUsageEvent({ metCriteria: SainAction.SOURCE_CLICKED, commonAttributes });
		},
		[fireAnalyticsEvent, commonAttributes, authorityUiMetadata, fireSAINUsageEvent],
	);

	const isBorderlessUX =
		fg('kd_team_26_sain_borderless_update') || SAINFeatureControl.isSAINUXUpliftEnabled();

	// URL patterns for sources known not to resolve as rich smart cards.
	const UNRESOLVABLE_URL_PATTERNS = ['viewpageattachments.action'];
	const isUnresolvableSource = (url: string) =>
		UNRESOLVABLE_URL_PATTERNS.some((pattern) => url.includes(pattern));

	// Filter sources whose URLs are known not to resolve as rich smart cards.
	// Uses URL pattern matching since the NlpSearchResultType enum does not include 'attachment'.
	// https://switcheroo.atlassian.com/ui/gates/f309f0ca-53a0-4c56-9767-fec898813e99/key/kd_sain_filter_attachment_sources
	const filteredSources = fg('kd_sain_filter_attachment_sources')
		? sources.filter((source) => !isUnresolvableSource(source.url))
		: sources;

	const sourcesSection = (
		<Stack space="space.100">
			{!isStreaming &&
				filteredSources.length > 0 &&
				SAINFeatureControl.isSourcesInShortSAINAnswersEnabled() && (
					<Box xcss={cx(styles.sourcesTopPadding, collapsed && styles.nonInteractive)}>
						{SAINFeatureControl.isLinkListSourcesEnabled() ? (
							<Stack space="space.100" xcss={styles.linkListContainer}>
								<Heading size="xsmall">
									<FormattedMessage {...messages.related_links_title} />
								</Heading>
								<LinkList
									urls={filteredSources.map((source) => source.url)}
									maxSources={5}
									showExpand={false}
									onLinkClick={(url, index, opts) => {
										const source = filteredSources[index];
										if (source) {
											sourceClickCallback({
												index,
												source,
												hostname: extractUrlHostname(url),
												sourceCardType: 'link-list',
												isAuxClick: opts?.isAuxClick,
												isContextMenu: opts?.isContextMenu,
											});
										}
									}}
								/>
							</Stack>
						) : (
							<AISourcesCarousel
								sources={filteredSources}
								searchSessionId={searchSessionId}
								onClickHandler={sourceClickCallback}
							/>
						)}
					</Box>
				)}
		</Stack>
	);

	const handleContainerKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (collapsed && (event.key === 'Enter' || event.key === ' ')) {
				event.preventDefault();
				handleContainerClick(event as unknown as React.MouseEvent<HTMLDivElement>);
			}
		},
		[collapsed, handleContainerClick],
	);

	return (
		<Box
			xcss={cx(
				collapsed ? styles.interactive : undefined,
				isBorderlessUX && styles.standardSAINMarginOverrides,
			)}
			onClick={handleContainerClick}
			onKeyDown={collapsed ? handleContainerKeyDown : undefined}
			tabIndex={collapsed ? 0 : undefined}
			ref={containerRef}
		>
			<Stack
				alignBlock="stretch"
				grow="fill"
				spread="space-between"
				xcss={isStreaming && !isBorderlessUX ? styles.root : styles.fullHeight}
			>
				<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
					<Box>
						{/* Conditional UI based on experiments */}
						<AICollapse
							title={
								<Inline
									spread="space-between"
									alignBlock="center"
									grow="fill"
									xcss={cx(
										(fg('kd_team_26_sain_borderless_update') ||
											SAINFeatureControl.isSAINUXUpliftEnabled()) &&
											styles.standardSAINMarginOverrides,
									)}
								>
									<Inline alignBlock="center" space="space.050" shouldWrap>
										{isHighlyAuthoritative && authorityUiMetadata ? (
											<>
												<Inline alignBlock="center" space="space.050">
													<StatusVerifiedIcon
														label=""
														size="medium"
														color={token('color.icon.success')}
													/>
													<SmartCardProvider>
														<AuthoritativeAnswerAttribution
															sources={authorityUiMetadata.sources ?? []}
															domainExperts={authorityUiMetadata.domainExperts ?? []}
															overallReasoning={authorityUiMetadata.overallReasoning}
															labelVariant="answer"
															cloudId={cloudId}
															surface="sain"
															// SAIN renders in the main results column with ample room, so
															// use the taller panel to avoid cramping the feedback form.
															panelSize="tall"
															onPanelOpen={handleAuthorityPanelShown}
															onSourceClick={() => fireAuthorityLinkClick('source')}
															onExpertClick={() => fireAuthorityLinkClick('expert')}
															onFeedbackSubmit={(submission) => {
																// Submit authority-answer feedback to the Feedback Collector API
																// using SAIN's existing entrypoint (same destination as SAIN's
																// footer feedback). Analytics wiring is handled separately.
																void submitAuthorityFeedback({
																	submission,
																	entrypointId: FEEDBACK_ENTRYPOINT_ID_SAIN,
																	origin: AUTHORITY_FEEDBACK_ORIGIN_SAIN,
																	cloudId,
																	debugAttributes: {
																		traceId: extraAttributes?.requestId,
																		searchSessionId,
																		queryHash,
																	},
																});
															}}
														/>
													</SmartCardProvider>
												</Inline>
											</>
										) : (
											<>
												<AiGenerativeTextSummaryIcon label="AI Content" size="medium" />
												<Heading as="h1" size="xsmall">
													<FormattedMessage {...messages.smart_answer_card_title} />
												</Heading>
											</>
										)}
									</Inline>
									<Inline alignBlock="center" space="space.100">
										{!collapsed && !isStreaming && (
											<Button
												spacing="compact"
												appearance="subtle"
												onClick={onHideAnswer}
												iconAfter={(iconProps) => (
													<EyeOpenStrikethroughIcon {...iconProps} size="small" />
												)}
											>
												<Text color="color.text.subtlest">
													{formatMessage(messages.hide_answer)}
												</Text>
											</Button>
										)}
									</Inline>
								</Inline>
							}
							isCollapsed={collapsed}
							onExpandCollapse={handleExpandCollapseButtonClick}
							ref={contentContainerRef}
							showReadMoreButton={isBorderlessUX ? collapsed : showReadMoreButton}
							luminaAnswerType={
								fg('kd_team_26_sain_borderless_update') ||
								SAINFeatureControl.isSAINUXUpliftEnabled()
									? 'STANDARD'
									: undefined
							}
						>
							<Box ref={renderedContentRef} id="ai-answer-result-section" testId="sain-fmp-target">
								<SearchAIAnalyticsContext
									data={getSearchAIAnalyticsContextData({
										location: SearchAIAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
										commonAttributes,
									})}
								>
									<SmartCardProvider>
										<Box ref={textContentRef}>
											<MemoizedRenderer
												contentToRender={contentToRender}
												showAnimation={showAnimation}
											/>
										</Box>
									</SmartCardProvider>
								</SearchAIAnalyticsContext>
								{!hasLinksInAnswer && sourcesSection}
							</Box>
						</AICollapse>

						{showReadMoreButton && !SAINFeatureControl.isSAINUXUpliftEnabled() && (
							<ReadMoreButton onClick={handleExpandCollapseButtonClick} />
						)}

						{(collapsed || showReadMoreButton || isStreaming) &&
							SAINFeatureControl.isSAINUXUpliftEnabled() && (
								<AnimatedSingleLineShowMore
									isStreaming={isStreaming}
									showMoreButtonVisible={collapsed || (isStreaming && !userExpandedDuringLoading)}
									fireShownEvent={isTextTruncated}
									onClickShowMoreButton={handleExpandCollapseButtonClick}
								/>
							)}
					</Box>

					{/* Sources section - only show when complete and not collapsed */}
					{!isStreaming &&
						!collapsed &&
						sources.length > 0 &&
						!SAINFeatureControl.isSourcesInShortSAINAnswersEnabled() &&
						!hasLinksInAnswer && (
							<Box xcss={styles.sectionContainer}>
								<AISourcesCarousel
									sources={sources}
									searchSessionId={searchSessionId}
									onClickHandler={sourceClickCallback}
								/>
							</Box>
						)}
					{SAINFeatureControl.isSAINUXUpliftEnabled() &&
						!isStreaming &&
						!collapsed &&
						sources.length > 0 && (
							<Box xcss={styles.followUpChatInputSectionWithBarStyles}>
								<SourcesSidePanel
									renderTrigger={(onToggle, triggerRef) => (
										<SourcesBar
											sources={sources}
											numSources={sources.length}
											onClick={onToggle}
											triggerRef={triggerRef}
										/>
									)}
								/>
							</Box>
						)}

					{/* Disclaimer section */}
					{!collapsed &&
						!showReadMoreButton &&
						!isStreaming &&
						SAINFeatureControl.isSAINUXUpliftEnabled() && (
							<Stack space="space.150" xcss={styles.followUpChatInputSectionStyles}>
								<Inline space="space.100" alignBlock="center">
									<FollowUpSuggestionFooter />
								</Inline>
								<OpenChatInputBox />
								<Box xcss={styles.disclaimerWrapperStyles}>
									<AIDisclaimerAndFeedback
										showFeedbackButtons={!isStreaming}
										showCopyButton={false}
										renderedContentRef={renderedContentRef}
									/>
								</Box>
							</Stack>
						)}

					{!SAINFeatureControl.isSAINUXUpliftEnabled() && (
						<Box xcss={styles.disclaimer}>
							<AIDisclaimerAndFeedback
								showFeedbackButtons={!isStreaming}
								showCopyButton={!isStreaming}
								renderedContentRef={renderedContentRef}
							/>
						</Box>
					)}
				</Stack>

				{/* Footer section - only show when complete */}
				{!isStreaming &&
				!SAINFeatureControl.isSAINUXUpliftEnabled() &&
				(!collapsed ||
					(errorStateSuggestedQueries.length > 0 && serveErrorStateSuggestedQueriesExp)) ? (
					<SmartAnswerFooterSection showOpenChat={true} />
				) : null}
			</Stack>
		</Box>
	);
}

export function SmartAnswerCardMinimized({
	onFMPReached,
	onAnswerExpandedChange,
	onAnswerMinimizedChange,
	queryIntent,
	floatingSmartAnswerReadyPortalRef,
}: SmartAnswerCardMinimizedProps): React.JSX.Element {
	return (
		<UFOSegment name="smart-answer-card">
			<SmartAnswerCardMinimizedComponent
				onFMPReached={onFMPReached}
				onAnswerExpandedChange={onAnswerExpandedChange}
				onAnswerMinimizedChange={onAnswerMinimizedChange}
				queryIntent={queryIntent}
				floatingSmartAnswerReadyPortalRef={floatingSmartAnswerReadyPortalRef}
			/>
		</UFOSegment>
	);
}
